import React, { useEffect, useState } from "react";
import { Formik, useFormikContext } from "formik";
import { CForm, CRow, CCol, CFormLabel, CFormSelect, CButton, CNav, CNavItem, CNavLink } from "@coreui/react";
import { useNavigate } from "react-router-dom";
import CustomInput from "@/components/CustomInput";
import CustomEditor from "@/components/CKEditor";
import { cms } from "@/constants/translation/cms.translation";
import { useTranslationContext } from "@/context/TranslationContext";
import { masters } from "@/libs/routes";
import constants from "@/libs/constant";

/**
 * Switches to the first language tab that has a validation error after a failed submit,
 * so the admin is never left looking at a valid tab while another tab blocks saving.
 */
const FocusFirstErrorTab = ({ setActiveTab }) => {
  const { errors, submitCount, isSubmitting } = useFormikContext();

  useEffect(() => {
    if (submitCount === 0 || isSubmitting) return;
    const list = errors?.[constants.formFields.cms.translations];
    if (!Array.isArray(list)) return;
    const firstIndex = list.findIndex((item) => !!item);
    if (firstIndex >= 0) setActiveTab(firstIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitCount]);

  return null;
};

/**
 * CmsForm Component
 * @param {Object} props
 * @param {boolean} props.isEdit - Edit mode: slug is shown read-only
 * @param {Object} props.initialValues - { slug, translations: [{ language_id, language_code, language_name, title, description }] }
 * @param {Function} props.onSubmit - Called with validated form values
 * @param {boolean} props.isSubmitting - True while the update request is in flight
 * @param {Object} props.validationSchema - Yup schema
 * @returns {JSX.Element}
 */
const CmsForm = ({ initialValues, onSubmit, isSubmitting = false, validationSchema, isEdit = false }) => {
  const t = useTranslationContext();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const fields = constants.formFields.cms;

  /** Discard changes and go back to the list. */
  const handleCancel = () => navigate(masters.cms.list);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values) => onSubmit(values)}
      enableReinitialize
      validationSchema={validationSchema}
    >
      {({ values, handleSubmit, handleChange, handleBlur, errors, touched, setFieldValue }) => (
        <CForm
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit(e);
          }}
        >
          <FocusFirstErrorTab setActiveTab={setActiveTab} />
          <CRow className="mb-2 m-auto">
            <CCol lg={12}>
              <CustomInput
                label={t(cms.form.slug_label)}
                type={constants.inputTypes.text}
                name={fields.slug}
                placeholder={t(cms.form.slug_placeholder)}
                value={values.slug}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isEdit}
                error={touched.slug && errors.slug}
              />
              {isEdit && <div className="text-muted small mb-2">{t(cms.form.slug_locked_hint)}</div>}
            </CCol>
            <CCol lg={12}>
              <CustomInput
                label={t(cms.form.version_label)}
                type={constants.inputTypes.text}
                name={fields.version}
                placeholder={t(cms.form.version_placeholder)}
                value={values.version}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.version && errors.version}
              />
              <div className="text-muted small mb-2">{t(cms.form.version_hint)}</div>
            </CCol>
            <CCol lg={12} className="mb-3">
              <CFormLabel htmlFor="cms-status">{t(cms.form.status_label)}</CFormLabel>
              <CFormSelect
                id="cms-status"
                name={fields.isActive}
                value={values.is_active}
                onChange={handleChange}
                disabled={isSubmitting}
              >
                <option value="true">{t(cms.form.status_active)}</option>
                <option value="false">{t(cms.form.status_inactive)}</option>
              </CFormSelect>
            </CCol>
          </CRow>
          <CNav variant="tabs" className="mb-3">
            {values.translations.map((lang, index) => (
              <CNavItem key={lang.language_id}>
                <CNavLink
                  href="#"
                  active={activeTab === index}
                  className={errors?.translations?.[index] && touched?.translations?.[index] ? "text-danger" : ""}
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveTab(index);
                  }}
                >
                  {lang.language_name}
                  {index === 0 ? " *" : ""}
                </CNavLink>
              </CNavItem>
            ))}
          </CNav>

          {values.translations.map((lang, index) => {
            if (index !== activeTab) return null;
            const base = `${fields.translations}.${index}`;
            const isPrimary = index === 0;
            return (
              <CRow key={lang.language_id} className="mb-2 m-auto p-3 border rounded">
                {!isPrimary && (
                  <CCol lg={12} className="mb-2 text-muted small">
                    {t(cms.form.optional_hint)}
                  </CCol>
                )}
                <CCol lg={12}>
                  <CustomInput
                    label={t(cms.form.title_label)}
                    type={constants.inputTypes.text}
                    name={`${base}.${fields.title}`}
                    placeholder={t(cms.form.title_placeholder)}
                    value={lang.title}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.translations?.[index]?.title && errors.translations?.[index]?.title}
                  />
                </CCol>
                <CCol lg={12}>
                  <CFormLabel>{t(cms.form.content_label)}</CFormLabel>
                  <CustomEditor
                    enableImageUpload
                    name={`${base}.${fields.description}`}
                    onChange={(html) => setFieldValue(`${base}.${fields.description}`, html)}
                  />
                </CCol>
                <CCol lg={12} className="mt-3">
                  <CustomInput
                    label={t(cms.form.seo_meta_title_label)}
                    type={constants.inputTypes.text}
                    name={`${base}.${fields.seoMetaTitle}`}
                    placeholder={t(cms.form.seo_meta_title_placeholder)}
                    value={lang.seo_meta_title}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.translations?.[index]?.seo_meta_title && errors.translations?.[index]?.seo_meta_title}
                  />
                </CCol>
                <CCol lg={12}>
                  <CustomInput
                    label={t(cms.form.seo_meta_desc_label)}
                    type={constants.inputTypes.textarea}
                    name={`${base}.${fields.seoMetaDesc}`}
                    placeholder={t(cms.form.seo_meta_desc_placeholder)}
                    value={lang.seo_meta_desc}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.translations?.[index]?.seo_meta_desc && errors.translations?.[index]?.seo_meta_desc}
                  />
                </CCol>
              </CRow>
            );
          })}

          <CRow className="text-center mt-3">
            <CCol lg={12} md={12} sm={12} className="d-flex gap-2 justify-content-center">
              <CButton
                color="primary"
                className="px-4 mt-3"
                type={constants.inputTypes.submit}
                disabled={isSubmitting}
              >
                {t(
                  isSubmitting
                    ? cms.form.loading_btn_text
                    : isEdit
                      ? cms.form.submit_btn_text
                      : cms.form.add_submit_btn_text,
                )}
              </CButton>
              <CButton
                color="secondary"
                className="px-4 mt-3"
                type="button"
                disabled={isSubmitting}
                onClick={handleCancel}
              >
                {t(cms.form.cancel_btn_text)}
              </CButton>
            </CCol>
          </CRow>
        </CForm>
      )}
    </Formik>
  );
};

export default CmsForm;
